import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { LoadingBlock, ErrorState } from '../components/States'
import { BackButton } from '../components/BackButton'
import { howLong } from './Benchmarking'
import type { RunProgress } from '../types'
import { statusWords, notPricedCount } from '../lib/not-priced'

/**
 * What a finished run says it did.
 *
 * Roles our Bedrock allowance turned away are said separately from roles that
 * could not be matched. On screen they read the same and they mean different
 * things: one is a role with no counterpart in the survey, the other is a role
 * we never got to ask about and can simply run again.
 */
function finishedLine(run: RunProgress): string {
  const parts = ['Finished.']
  if (run.noMatch > 0) parts.push(notPricedCount(run.noMatch))
  const ours = Math.max(run.failed - (run.throttled ?? 0), 0)
  if (ours > 0) parts.push(`${ours} could not be matched.`)
  if (run.throttled > 0) {
    parts.push(
      run.throttled === 1
        ? '1 was not reached because we were busy. Run it again and it should go through.'
        : `${run.throttled} were not reached because we were busy. Run them again and they should go through.`,
    )
  }
  return parts.join(' ')
}

/**
 * A run, while it runs.
 *
 * **It says the page can be closed, in those words.** A run of twenty-five
 * minutes with only a spinner is a run somebody sits and watches.
 *
 * Results appear one at a time underneath, so progress reads as work being
 * done rather than as a number climbing. Failures appear in place with their
 * reason, not collected into a summary at the end where nobody reads them.
 *
 * **Time left, never money spent.**
 */
export function BenchmarkRun() {
  const { runId = '' } = useParams()
  const navigate = useNavigate()
  /*
   * Where to go when this finishes.
   *
   * A run started from one role's Benchmark button is a step on the way to
   * that role's shortlist, not a destination. Leaving the person on a
   * finished progress bar makes them find their way back to the thing they
   * asked for.
   */
  const [params] = useSearchParams()
  const then = params.get('then') ?? ''
  const [run, setRun] = useState<RunProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    let timer: ReturnType<typeof setTimeout>

    const poll = async () => {
      try {
        const out = await api.benchmarkRun(runId)
        if (!live.current) return
        setRun(out)
        setError(null)
        // Stops polling once it is over. A finished run that keeps asking is a
        // tab quietly making requests for the rest of the afternoon.
        if (out.state === 'running' || out.state === 'stopping') {
          timer = setTimeout(poll, 2000)
        } else if (then) {
          navigate(`/benchmarking/${then}`, { replace: true })
        }
      } catch (err) {
        if (!live.current) return
        setError(err instanceof Error ? err.message : 'We lost track of this run.')
      }
    }
    void poll()
    return () => { live.current = false; clearTimeout(timer) }
  }, [runId])

  if (error && !run) return <ErrorState message={error} onRetry={() => navigate(0)} />
  if (!run) return <LoadingBlock label="Finding this run" />

  const done = run.done
  const percent = run.total === 0 ? 100 : Math.round((done / run.total) * 100)
  const over = run.state === 'done' || run.state === 'stopped'

  async function stop() {
    setStopping(true)
    try {
      await api.stopBenchmarkRun(runId)
    } finally {
      setStopping(false)
    }
  }

  return (
    <section className="screen">
      {/*
        The way out, from the moment the screen opens.

        **A run of forty-eight roles takes about ten minutes**, and this
        screen used to offer nothing until it was over. Somebody who started a
        run and wanted to go and do something else had only the browser's own
        button, which is the moment they stop believing the product has
        thought about them.

        The run is not this screen: leaving does not stop it, and the line
        below already says so.
      */}
      <BackButton to="benchmarking" onClick={() => navigate('/benchmarking')} />
      <section className="card card--pad">
        <div className="progress__head">
          <h2>
            {done.toLocaleString()} of {run.total.toLocaleString()} done
          </h2>
          {!over && (
            <button type="button" className="btn" onClick={stop} disabled={stopping || run.state === 'stopping'}>
              {/*
                * Several roles run at once now, so stopping cannot mean
                * stopping after the one role somebody is watching. It means
                * no new role starts and the ones already going will finish.
                * Saying "after this one" would look like it had hung while
                * the rest of the roles in flight came in.
                */}
              {run.state === 'stopping' ? 'Stopping, finishing what started' : 'Stop after the ones running'}
            </button>
          )}
        </div>

        {/* Announced as well as drawn, so finishing is not silent for anyone
            who is not looking at the bar. */}
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={done}
          aria-valuemin={0}
          aria-valuemax={run.total}
          aria-label={`${done} of ${run.total} job descriptions benchmarked`}
        >
          <div className="progress__bar" style={{ width: `${percent}%` }} />
        </div>

        <p aria-live="polite" className="subline">
          {/*
            **A run being stopped said it was carrying on.**

            This line had two states, going and over, so a run somebody had
            just stopped still read "About 8 minutes left. You can close
            this page. The run carries on and finishes on its own", under a
            button that said "Stopping, finishing what started". The screen
            contradicted itself and the reading people took from it was that
            stop had not worked.

            Stopping is its own state: the roles already going will finish
            and no new one will start. No time is offered, because what is
            left is however long the few in flight take, and a number that
            was worked out for the whole run is not that.
          */}
          {over
            ? run.state === 'done'
              ? finishedLine(run)
              : 'Stopped. Everything that finished is saved.'
            : run.state === 'stopping'
              ? 'Stopping. The roles already started will finish, and no new one will begin. Everything done is saved.'
              : `About ${howLong(run.secondsLeft)} left. You can close this page. The run carries on and finishes on its own.`}
        </p>

        {over && (
          <div className="actions" style={{ marginTop: 8 }}>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/benchmarking')}>
              Back to benchmarking
            </button>
          </div>
        )}
      </section>

      <section className="card card--pad">
        <h3>As they finish</h3>
        {run.recent.length === 0 ? (
          <p className="subline">Nothing has finished yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Job description</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {run.recent.map((item) => (
                <tr key={item.jdId}>
                  <td>
                    <a href={`/benchmarking/${item.jdId}`} onClick={(e) => { e.preventDefault(); navigate(`/benchmarking/${item.jdId}`) }}>
                      {item.title}
                    </a>
                  </td>
                  <td>{statusWords(item.outcome)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="hint">
        Started by {run.startedBy}, {new Date(run.startedAt).toLocaleString()}.
      </p>
    </section>
  )
}
